import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, HardHat, FlameKindling, Syringe, Tractor, HeartPulse, MoonStar } from "lucide-react";
import { MaskLine, FadeUp } from "../components/Reveal";
import { LogoLockup } from "../components/Logo";
import { VINEOPS_URL } from "../constants/site";

const MODULES = [
  [HardHat, "Weekly Tailgate Meetings", "Bilingual (English/Spanish) safety meetings held with every crew, every week, with topics matched to the work happening that day."],
  [FlameKindling, "Heat Illness Prevention", "Shade, water, and rest-break protocols with acclimatization schedules, triggered by forecast and field temperature."],
  [Syringe, "Pesticide & Handler Safety", "EPA Worker Protection Standard training, PPE issuance and inspection, and strict re-entry interval enforcement on every application."],
  [Tractor, "Equipment & Tractor Operation", "Operator authorization, pre-operation checklists, and rollover and guarding standards for every machine on the ranch."],
  [HeartPulse, "First Aid & CPR", "Certified field supervisors assigned to every crew, with kits and emergency action plans posted at each site."],
  [MoonStar, "Harvest & Night Operations", "Lighting plans, fatigue management, and traffic control for night picks and heavy harvest traffic."],
];

const STANDARDS = [
  ["Cal/OSHA IIPP", "A written Injury & Illness Prevention Program, maintained and reviewed annually."],
  ["EPA Worker Protection Standard", "WPS training current for every worker and handler, with central posting at each ranch."],
  ["Licensed Application Oversight", "Pest-control recommendations and applications supervised by licensed professionals."],
  ["Certified Field Supervisors", "CPR and First Aid certification held by supervising staff on every crew."],
];

const RECORDS = [
  ["Training logs", "Date, topic, trainer, and signed roster for every session"],
  ["Application records", "Material, rate, block, REI, and operator for every spray"],
  ["Inspection records", "Equipment checks, PPE issuance, and site inspections"],
  ["Incident reports", "Documented, investigated, and closed out in writing"],
];

export default function SafetyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Safety Training & Compliance | Vinedresser Vineyard Management, Napa & Sonoma";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "Vineyard safety training and compliance record keeping for Napa and Sonoma estates: tailgate meetings, heat illness prevention, pesticide safety, equipment training, and audit-ready records in VineOps.");
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div data-testid="safety-page">
      <header className="border-b border-[#E5DEC9] bg-[#F9F6F0]">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-12 h-[72px]">
          <Link to="/" data-testid="safety-logo-link">
            <LogoLockup size="sm" />
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="safety-back-link"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#364132] hover:text-[#B89B67] transition-colors duration-300"
            >
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
              Back to site
            </Link>
            <a
              data-testid="safety-client-login"
              href={VINEOPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2.5 text-[11px] tracking-[0.2em] uppercase font-semibold px-5 py-3 border border-[#1A3626] text-[#1A3626] transition-all duration-300 hover:bg-[#B89B67] hover:border-[#B89B67] hover:text-[#12271B]"
            >
              <img src="/assets/vineops-mark.png" alt="VineOps" className="h-[11px] w-auto" />
              Client Login
            </a>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#B89B67] font-semibold mb-10">Safety &amp; Compliance</p>
          </FadeUp>
          <h1 className="font-display font-light text-[#1A3626] text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[14ch]">
            <MaskLine delay={0.1}>Safe crews.</MaskLine>
            <MaskLine delay={0.22}>Clean records.</MaskLine>
          </h1>
          <FadeUp delay={0.35} className="mt-10 max-w-[54ch]">
            <p className="text-base md:text-lg leading-relaxed text-[#2C2926]/80">
              High-end farming starts with the people doing it. Vinedresser runs a documented safety and compliance program across every managed ranch in Napa and Sonoma, with training that happens on schedule and records that hold up to any inspection.
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="safety-training-section" className="pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <FadeUp>
            <h2 className="font-display font-light text-3xl md:text-4xl text-[#1A3626] mb-12">The Training Program</h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-px bg-[#E5DEC9] border border-[#E5DEC9]">
            {MODULES.map(([Icon, title, body], i) => (
              <FadeUp key={title} delay={(i % 3) * 0.08} y={24}>
                <div data-testid={`safety-module-${i + 1}`} className="bg-[#F9F6F0] p-10 h-full hover:bg-[#F3EFE6] transition-colors duration-500">
                  <Icon size={20} className="text-[#B89B67]" strokeWidth={1.5} />
                  <h3 className="font-display font-light text-2xl text-[#1A3626] mt-6 mb-3">{title}</h3>
                  <p className="text-sm leading-relaxed text-[#2C2926]/70">{body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="safety-records-section" className="bg-[#1A3626] text-[#F9F6F0] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <FadeUp>
              <img src="/assets/logo-vineops-light.png" alt="VineOps by Vinedresser Vineyard Management, Inc." className="w-52 md:w-60 h-auto mb-10" />
            </FadeUp>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight">
              <MaskLine delay={0.1}>Every session,</MaskLine>
              <MaskLine delay={0.22}>on the record.</MaskLine>
            </h2>
            <FadeUp delay={0.35} className="mt-8 max-w-[44ch]">
              <p className="text-base leading-relaxed text-[#F9F6F0]/75">
                Training rosters, topics, and trainer sign-offs are logged in VineOps the day they happen. When Cal/OSHA or the county agricultural commissioner asks, the answer is already filed.
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-7">
            <FadeUp delay={0.25}>
              <div data-testid="safety-records-card" className="border border-[#F9F6F0]/15 bg-[#12271B]">
                <div className="flex items-center justify-between px-8 py-5 border-b border-[#F9F6F0]/10">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-[#F9F6F0]/60">Compliance Records</span>
                  <ShieldCheck size={16} className="text-[#B89B67]" strokeWidth={1.5} />
                </div>
                <div className="px-8 py-4">
                  {RECORDS.map(([label, value]) => (
                    <div key={label} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-4 border-b border-[#F9F6F0]/8 last:border-0">
                      <span className="text-xs tracking-[0.12em] uppercase text-[#F9F6F0]/55">{label}</span>
                      <span className="text-sm text-[#F9F6F0]/90 sm:text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="safety-standards-section" className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <FadeUp>
            <h2 className="font-display font-light text-3xl md:text-4xl text-[#1A3626] mb-12">Standards We Farm To</h2>
          </FadeUp>
          <div>
            {STANDARDS.map(([title, body], i) => (
              <FadeUp key={title} delay={i * 0.06}>
                <div data-testid={`safety-standard-${i + 1}`} className="hairline-t grid md:grid-cols-12 gap-4 py-8 group">
                  <div className="md:col-span-4">
                    <h3 className="font-display font-light text-2xl text-[#1A3626] transition-transform duration-500 group-hover:translate-x-2">{title}</h3>
                  </div>
                  <div className="md:col-span-8">
                    <p className="text-sm leading-relaxed text-[#2C2926]/70 max-w-[60ch]">{body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
            <div className="hairline-t" />
          </div>

          <FadeUp delay={0.2} className="mt-16 flex flex-col sm:flex-row sm:items-center gap-6">
            <Link
              to="/#contact"
              data-testid="safety-cta-button"
              className="inline-flex items-center justify-center gap-3 bg-[#1A3626] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase font-semibold px-10 py-4 transition-colors duration-300 hover:bg-[#B89B67] hover:text-[#12271B] active:scale-95"
            >
              Ask about our program
            </Link>
            <p className="text-xs text-[#364132]/60 max-w-[42ch]">
              Certifications and program details are verified during onboarding. Documentation available on request.
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
